/**
 * PINNED CREATION WITH STATUS COMPONENT
 * 
 * Shows a pinned creation in conversation with status.
 * Features:
 * - Click to view creation details (via callback to parent)
 * - Sellers can change the status
 * - Either participant can remove the creation from conversation
 * - Shows "Suggested by seller" label when seller added the creation
 */

import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { updateCreationStatus, removeCreationFromConversation } from '../../messaging/messagingService';
import type { AttachedCreation } from '../../messaging/types';
import { ChevronDown, Loader2, X, ExternalLink, Sparkles } from 'lucide-react';
import { AppIcons } from '../../constants/icons';

interface PinnedCreationWithStatusProps {
  creation: AttachedCreation;
  conversationId: string;
  currentUserId: string;
  isOwner: boolean; // Is the current user the owner of this creation?
  onViewDetails?: (creation: AttachedCreation) => void; // Callback when user wants to view creation details
}

const PinnedCreationWithStatus: React.FC<PinnedCreationWithStatusProps> = ({
  creation,
  conversationId,
  currentUserId,
  isOwner,
  onViewDetails
}) => {
  const [isChanging, setIsChanging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStatus = creation.status || 'available';

  const statusOptions = [
    { value: 'available', label: 'Available', icon: AppIcons.Available, color: 'bg-green-500', iconColor: 'text-green-500' },
    { value: 'pending', label: 'Pending', icon: AppIcons.Pending, color: 'bg-yellow-500', iconColor: 'text-yellow-500' },
    { value: 'sold', label: 'Sold', icon: AppIcons.Sold, color: 'bg-red-500', iconColor: 'text-red-500' },
  ];

  const currentStatusInfo = statusOptions.find(s => s.value === currentStatus) || statusOptions[0];

  // Get category display
  const getCategoryInfo = () => {
    switch (creation.category) {
      case 'pressons': return { icon: AppIcons.PressOns, label: 'Press-Ons' };
      case 'jewelry': return { icon: AppIcons.Jewelry, label: 'Jewelry' };
      case 'other': return { icon: AppIcons.Creation, label: 'Other' };
      default: return { icon: AppIcons.Creation, label: 'Creation' };
    }
  };

  const categoryInfo = getCategoryInfo();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle clicking to view creation details
  const handleCreationClick = () => {
    if (onViewDetails) {
      onViewDetails(creation);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus || isChanging) return;
    
    setIsChanging(true);
    setShowDropdown(false);

    try {
      // 1. Update the creation in the owner's collection
      const creationRef = doc(db, 'users', creation.ownerId, 'creations', creation.creationId);
      await updateDoc(creationRef, {
        marketplaceStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      // 2. Update status in conversation and send system message
      await updateCreationStatus(
        conversationId,
        currentUserId,
        creation.creationId,
        creation.title,
        currentStatus,
        newStatus
      );

    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  const handleRemove = async () => {
    if (isRemoving) return;
    
    setIsRemoving(true);
    setShowRemoveConfirm(false);

    try {
      await removeCreationFromConversation(
        conversationId,
        currentUserId,
        creation.creationId,
        creation.title
      );
    } catch (err) {
      console.error('Error removing creation:', err);
      alert('Failed to remove creation. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isChanging) {
      setShowDropdown(!showDropdown);
    }
  };

  return (
    <div className="glass-card-sm border-purple-500/50 overflow-visible relative group" style={{ width: '140px' }}>
      {/* Remove button - shows on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowRemoveConfirm(true);
        }}
        disabled={isRemoving}
        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-md"
        title="Remove from conversation"
      >
        {isRemoving ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <X className="icon-xs" />
        )}
      </button>

      {/* Remove confirmation overlay */}
      {showRemoveConfirm && (
        <div className="absolute inset-0 bg-slate-800/95 backdrop-blur-sm rounded-xl z-20 flex flex-col items-center justify-center p-2">
          <p className="text-xs text-glass-secondary text-center mb-2 font-body">
            Remove this creation?
          </p>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowRemoveConfirm(false);
              }}
              className="px-2 py-1 text-xs text-glass-secondary hover:bg-slate-700 rounded transition-default font-body"
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="px-2 py-1 text-xs bg-red-500 text-white hover:bg-red-600 rounded transition-default font-body"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Clickable Image */}
      <div 
        onClick={handleCreationClick}
        className={`relative group/image ${onViewDetails ? 'cursor-pointer' : ''}`}
      >
        {creation.photoUrl ? (
          <img 
            src={creation.photoUrl} 
            alt={creation.title}
            className="w-full h-20 object-cover rounded-t-lg"
          />
        ) : (
          <div className="w-full h-20 bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center rounded-t-lg">
            <AppIcons.Creation className="w-8 h-8 text-white" aria-label="Creation" />
          </div>
        )}

        {/* Badges container - stacked on top-left */}
        <div className="absolute top-1 left-1 flex flex-col gap-0.5">
          {/* Category badge */}
          <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-white/90 text-purple-600">
            <categoryInfo.icon className="w-2.5 h-2.5 text-current" aria-label={categoryInfo.label} />
            {categoryInfo.label}
          </span>
          
          {/* Suggested by seller badge */}
          {creation.suggestedBy === 'seller' && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-700 border border-amber-300">
              <Sparkles className="w-2.5 h-2.5" />
              Suggested
            </span>
          )}
        </div>
        
        {/* View creation overlay on hover */}
        {onViewDetails && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/image:opacity-100 transition-opacity rounded-t-lg flex items-center justify-center">
            <div className="flex items-center gap-1 text-white text-xs font-bold">
              <ExternalLink className="icon-xs" />
              View Creation
            </div>
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-2">
        <p
          onClick={handleCreationClick}
          className={`text-sm font-bold text-glass truncate mb-1 font-body ${onViewDetails ? 'cursor-pointer hover:underline' : ''}`}
        >
          {creation.title}
        </p>
        {creation.price && (
          <p className="text-xs text-green-600 font-bold mb-2 font-body">
            ${creation.price.toFixed(2)}
          </p>
        )}
        
        {/* Status - Dropdown for owner, Badge for others */}
        {isOwner ? (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={toggleDropdown}
              disabled={isChanging}
              className={`w-full flex items-center justify-between gap-1 px-2 py-1.5 rounded text-xs font-bold text-white font-body ${currentStatusInfo.color} hover:opacity-90 transition-opacity cursor-pointer`}
            >
              {isChanging ? (
                <Loader2 className="w-3 h-3 animate-spin mx-auto" />
              ) : (
                <>
                  <span className="flex items-center gap-1">
                    <currentStatusInfo.icon className="w-3 h-3 text-current fill-current" aria-label={currentStatusInfo.label} />
                    {currentStatusInfo.label}
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>
            
            {/* Dropdown */}
            {showDropdown && !isChanging && (
              <div
                className="glass-dropdown absolute left-0 right-0 mt-1 overflow-hidden"
                style={{
                  zIndex: 9999,
                  top: '100%'
                }}
              >
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusChange(option.value);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs text-glass hover:bg-slate-700 flex items-center gap-2 transition-default font-body ${
                      option.value === currentStatus ? 'bg-slate-700 font-bold' : ''
                    }`}
                  >
                    <option.icon className={`w-3 h-3 ${option.iconColor} fill-current`} aria-label={option.label} />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`px-2 py-1.5 rounded text-xs font-bold text-white text-center font-body ${currentStatusInfo.color} flex items-center justify-center gap-1`}
          >
            <currentStatusInfo.icon className="w-3 h-3 text-current fill-current" aria-label={currentStatusInfo.label} />
            {currentStatusInfo.label}
          </div>
        )}
      </div>
    </div>
  );
};

export default PinnedCreationWithStatus;
